import React, { useEffect, useState } from 'react';
import { ReportAction, ReportCircle } from '../../../services/moderationApi';

interface ReportActionsProps {
  reportedName: string;
  sharedCircles: ReportCircle[];
  reportedUserId: string;
  busy: boolean;
  onAction: (action: ReportAction, options: { notes?: string; circleIds?: string[] }) => void;
}

/** Dismiss, warn, or ban the reported user from circles they share with the reporter. */
export const ReportActions: React.FC<ReportActionsProps> = ({
  reportedName, sharedCircles, reportedUserId, busy, onAction,
}) => {
  const [notes, setNotes] = useState('');
  const [circleIds, setCircleIds] = useState<string[]>([]);
  // The owner of a circle cannot be banned from it.
  const bannable = sharedCircles.filter(circle => circle.owner_id !== reportedUserId);

  useEffect(() => {
    setNotes('');
    setCircleIds([]);
  }, [reportedUserId, sharedCircles]);

  const toggleCircle = (id: string) =>
    setCircleIds(current => (current.includes(id) ? current.filter(c => c !== id) : [...current, id]));

  const run = (action: ReportAction, question: string) => {
    if (!confirm(question)) return;
    onAction(action, { notes: notes.trim() || undefined, circleIds: action === 'ban' ? circleIds : undefined });
  };

  return (
    <div className="border-t pt-4 space-y-4">
      <h4 className="text-sm font-medium text-gray-500">Take action</h4>
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Notes (kept with the report, not sent to the user)"
        rows={2}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => run('dismiss', 'Dismiss this report with no action?')}
          disabled={busy}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition-colors disabled:opacity-50"
        >
          Dismiss
        </button>
        <button
          onClick={() => run('warn', `Email a warning to ${reportedName}?`)}
          disabled={busy}
          className="px-4 py-2 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-md font-medium transition-colors disabled:opacity-50"
        >
          Warn user
        </button>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-500 mb-2">Ban from a shared circle</h4>
        {bannable.length === 0 ? (
          <p className="text-sm text-gray-500">
            These two people share no circle that {reportedName} can be banned from.
          </p>
        ) : (
          <>
            <div className="space-y-1 mb-3">
              {bannable.map(circle => (
                <label key={circle.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={circleIds.includes(circle.id)}
                    onChange={() => toggleCircle(circle.id)}
                  />
                  {circle.name}
                </label>
              ))}
            </div>
            <button
              onClick={() => run('ban', `Remove ${reportedName} from ${circleIds.length} circle(s) and stop them rejoining?`)}
              disabled={busy || circleIds.length === 0}
              className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded-md font-medium transition-colors disabled:opacity-50"
            >
              Ban from selected
            </button>
          </>
        )}
      </div>
    </div>
  );
};
